import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  MessagesSquare,
  Users as UsersIcon,
  Shield,
  Sliders,
  Gift,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMe } from '@/lib/queries';
import { useCan } from '@/lib/use-can';

interface NavItem {
  path: string;
  label: string;
  Icon: typeof LayoutDashboard;
  perm?: string;
}

const staticNav: NavItem[] = [{ path: '/', label: 'Dashboard', Icon: LayoutDashboard }];

const moduleIcons: Record<string, typeof LayoutDashboard> = {
  tickets: MessagesSquare,
  users: UsersIcon,
  announcements: MessagesSquare,
  giveaways: Gift,
  roles: Shield,
};

export function Sidebar() {
  const me = useMe();
  const can = useCan();
  const moduleNav: NavItem[] =
    me.data?.modules.flatMap((m) =>
      m.nav.map((n) => ({
        path: n.path,
        label: n.label,
        Icon: moduleIcons[m.id] ?? LayoutDashboard,
      })),
    ) ?? [];

  const settingsNav: NavItem[] = [
    { path: '/settings/server', label: 'Server', Icon: Sliders, perm: 'settings.server' },
    { path: '/settings/audit', label: 'Audit log', Icon: Sliders, perm: 'settings.audit' },
    { path: '/settings/admins', label: 'Admins', Icon: Shield, perm: 'settings.admins' },
    { path: '/settings/modules', label: 'Modules', Icon: Sliders, perm: 'settings.modules' },
  ].filter((n) => !n.perm || can(n.perm));

  return (
    <aside className="flex h-screen w-60 flex-col border-r border-border bg-card">
      <div className="flex h-14 items-center gap-2 border-b border-border px-6 font-semibold tracking-tight">
        <img src="/favicon.svg" alt="" className="h-6 w-6" />
        Raynex
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        <NavSection items={staticNav} />
        {moduleNav.length > 0 && (
          <>
            <Separator />
            <NavSection items={moduleNav} />
          </>
        )}
        <Separator />
        <NavSection items={settingsNav} />
      </nav>
    </aside>
  );
}

function NavSection({ items }: { items: NavItem[] }) {
  return (
    <div className="space-y-0.5">
      {items.map(({ path, label, Icon }) => (
        <NavLink
          key={path}
          to={path}
          end={path === '/'}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
              isActive
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
            )
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </div>
  );
}

function Separator() {
  return <div className="my-2 h-px bg-border" />;
}
